import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Key, Copy, Check, Trash2, Plus,
  Lock, CheckCircle,
  BarChart2, Shield,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';

// TODO: replace with real GET /api/v1/profiler/api-keys endpoint

// ── Types ─────────────────────────────────────────────────────────────────────

type KeyTier = 'BASIC' | 'PROFESSIONAL' | 'ENTERPRISE';
type KeyPermission = 'cdd' | 'edd' | 'dossier' | 'social' | 'bulk_export' | 'webhooks';

interface ApiKey {
  id: string;
  name: string;
  tier: KeyTier;
  active: boolean;
  key_value: string;        // full value — masked in UI
  permissions: KeyPermission[];
  usage_count: number;
  monthly_limit: number;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TIER_COLORS: Record<KeyTier, { bg: string; text: string; border: string }> = {
  BASIC:        { bg: '#001A2E', text: '#00C8FF', border: '#00C8FF30' },
  PROFESSIONAL: { bg: '#1A0030', text: '#A855F7', border: '#A855F730' },
  ENTERPRISE:   { bg: '#1A0A00', text: '#F0B429', border: '#F0B42930' },
};

const PERMISSION_LABELS: Record<KeyPermission, string> = {
  cdd:         'CDD Checks',
  edd:         'EDD Checks',
  dossier:     'Dossier PDF',
  social:      'Social Scraping',
  bulk_export: 'Bulk Export',
  webhooks:    'Webhooks',
};

const TIER_DEFAULT_PERMS: Record<KeyTier, KeyPermission[]> = {
  BASIC:        ['cdd'],
  PROFESSIONAL: ['cdd', 'edd', 'dossier', 'webhooks'],
  ENTERPRISE:   ['cdd', 'edd', 'dossier', 'social', 'bulk_export', 'webhooks'],
};

const PERMISSIONS_MATRIX: { feature: string; basic: boolean | 'limited'; pro: boolean; ent: boolean; leaOnly?: boolean }[] = [
  { feature: 'CDD checks',       basic: true,      pro: true,  ent: true  },
  { feature: 'EDD checks',       basic: false,     pro: true,  ent: true  },
  { feature: 'Dossier PDF',      basic: false,     pro: true,  ent: true  },
  { feature: 'Social scraping',  basic: false,     pro: false, ent: true,  leaOnly: true },
  { feature: 'Full dossier',     basic: 'limited', pro: true,  ent: true  },
  { feature: 'API access',       basic: true,      pro: true,  ent: true  },
  { feature: 'Webhooks',         basic: false,     pro: true,  ent: true  },
  { feature: 'Bulk export',      basic: false,     pro: false, ent: true  },
];

const EXAMPLE_KEY: ApiKey = {
  id: 'key_001',
  name: 'Production KYC',
  tier: 'PROFESSIONAL',
  active: true,
  key_value: 'sk-syck-prd-9f3a2b1c8d7e6f4a5b9c3d2e1f0a8b7c',
  permissions: ['cdd', 'edd', 'dossier', 'webhooks'],
  usage_count: 284,
  monthly_limit: 500,
  created_at: new Date(Date.now() - 14 * 86400 * 1000).toISOString(),
};

function generateKeyValue(): string {
  const chars = 'abcdef0123456789';
  const seg = (n: number) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `sk-syck-${seg(4)}-${seg(8)}${seg(8)}${seg(8)}`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function permLabel(perms: KeyPermission[]): string {
  if (perms.includes('dossier')) return 'CDD + EDD + Dossier';
  if (perms.includes('edd')) return 'CDD + EDD';
  return 'CDD only';
}

function maskKey(val: string): string {
  if (val.length < 12) return '••••••••••••';
  return `${val.slice(0, 8)}${'•'.repeat(20)}`;
}

// ── ApiKeyCard ────────────────────────────────────────────────────────────────

function ApiKeyCard({
  apiKey,
  onRevoke,
}: {
  apiKey: ApiKey;
  onRevoke: (id: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [showRevoke, setShowRevoke] = useState(false);
  const copiedRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { bg, text, border } = TIER_COLORS[apiKey.tier];
  const pct = Math.round((apiKey.usage_count / apiKey.monthly_limit) * 100);

  function handleCopy() {
    navigator.clipboard.writeText(apiKey.key_value).catch(() => {});
    setCopied(true);
    if (copiedRef.current) clearTimeout(copiedRef.current);
    copiedRef.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
      <div
        style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10 }}
        className="p-5 flex flex-col gap-4"
      >
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Key size={16} style={{ color: text }} />
            <div>
              <span className="font-mono text-[12px] font-bold" style={{ color: '#DCE8F5' }}>{apiKey.name}</span>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full tracking-wider"
                  style={{ background: bg, color: text, border: `1px solid ${border}` }}
                >
                  {apiKey.tier}
                </span>
                <span
                  className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: apiKey.active ? '#00C85318' : '#18203080',
                    color: apiKey.active ? '#00C853' : '#5A7A9A',
                    border: `1px solid ${apiKey.active ? '#00C85340' : '#182030'}`,
                  }}
                >
                  {apiKey.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowRevoke(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded font-mono text-[9px] font-bold tracking-wider transition-all"
            style={{ background: 'transparent', color: '#FF1F1F', border: '1px solid #FF1F1F30' }}
          >
            <Trash2 size={10} /> Revoke
          </button>
        </div>

        {/* Key value */}
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
          style={{ background: '#090D13', border: '1px solid #182030' }}
        >
          <code className="flex-1 font-mono text-[11px]" style={{ color: '#5A7A9A', letterSpacing: '0.05em' }}>
            {maskKey(apiKey.key_value)}
          </code>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded font-mono text-[9px] font-bold tracking-wider transition-all"
            style={{
              background: copied ? '#00C85318' : '#0F1520',
              color: copied ? '#00C853' : '#5A7A9A',
              border: `1px solid ${copied ? '#00C85330' : '#182030'}`,
            }}
          >
            {copied ? <Check size={10} /> : <Copy size={10} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* Permissions */}
        <p className="text-[10px] font-mono" style={{ color: '#5A7A9A' }}>
          Permissions: <span style={{ color: '#DCE8F5' }}>{permLabel(apiKey.permissions)}</span>
        </p>

        {/* Usage */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>MONTHLY USAGE</span>
            <span className="text-[9px] font-mono" style={{ color: '#5A7A9A' }}>
              {apiKey.usage_count.toLocaleString()} / {apiKey.monthly_limit.toLocaleString()}
            </span>
          </div>
          <div className="h-1 rounded-full overflow-hidden" style={{ background: '#182030' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, pct)}%`,
                background: pct > 85 ? '#FF1F1F' : pct > 65 ? '#F0B429' : '#00C8FF',
              }}
            />
          </div>
          <span className="text-[9px] font-mono" style={{ color: '#3A5A7A' }}>{pct}% used</span>
        </div>
      </div>

      {/* Revoke confirmation */}
      <Dialog open={showRevoke} onOpenChange={setShowRevoke}>
        <DialogContent style={{ background: '#0C1118', border: '1px solid #182030', color: '#DCE8F5' }}>
          <DialogHeader>
            <DialogTitle className="font-mono text-[13px] tracking-widest">Revoke API Key</DialogTitle>
            <DialogDescription className="font-mono text-[11px]" style={{ color: '#5A7A9A' }}>
              This will permanently revoke <strong style={{ color: '#DCE8F5' }}>{apiKey.name}</strong>. Any integrations using this key will stop working immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRevoke(false)}
              className="font-mono text-[10px] border-[#182030] text-[#5A7A9A] bg-transparent hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { onRevoke(apiKey.id); setShowRevoke(false); }}
              className="font-mono text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
            >
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── NewKeyModal ───────────────────────────────────────────────────────────────

function NewKeyModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (key: ApiKey) => void;
}) {
  const [name, setName]           = useState('');
  const [tier, setTier]           = useState<KeyTier>('BASIC');
  const [perms, setPerms]         = useState<Set<KeyPermission>>(new Set(TIER_DEFAULT_PERMS.BASIC));
  const [rateLimit, setRateLimit] = useState(60);
  const [createdKey, setCreatedKey] = useState<ApiKey | null>(null);
  const [copiedNew, setCopiedNew]   = useState(false);
  const copiedRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleTierChange(t: KeyTier) {
    setTier(t);
    setPerms(new Set(TIER_DEFAULT_PERMS[t]));
  }

  function togglePerm(p: KeyPermission) {
    setPerms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  }

  function handleCreate() {
    if (!name.trim()) return;
    const newKey: ApiKey = {
      id: `key_${Date.now()}`,
      name: name.trim(),
      tier,
      active: true,
      key_value: generateKeyValue(),
      permissions: [...perms],
      usage_count: 0,
      monthly_limit: tier === 'BASIC' ? 100 : tier === 'PROFESSIONAL' ? 500 : 2000,
      created_at: new Date().toISOString(),
    };
    setCreatedKey(newKey);
  }

  function handleFinish() {
    if (createdKey) onCreate(createdKey);
    setName(''); setTier('BASIC'); setPerms(new Set(TIER_DEFAULT_PERMS.BASIC));
    setRateLimit(60); setCreatedKey(null); setCopiedNew(false);
    onClose();
  }

  function handleCopyNew() {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey.key_value).catch(() => {});
    setCopiedNew(true);
    if (copiedRef.current) clearTimeout(copiedRef.current);
    copiedRef.current = setTimeout(() => setCopiedNew(false), 2000);
  }

  const TIER_OPTS: KeyTier[] = ['BASIC', 'PROFESSIONAL', 'ENTERPRISE'];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !createdKey) onClose(); }}>
      <DialogContent style={{ background: '#0C1118', border: '1px solid #182030', color: '#DCE8F5', maxWidth: 520 }}>
        {!createdKey ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-mono text-[13px] tracking-widest">New API Key</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-5 py-2">
              {/* Key name */}
              <div className="flex flex-col gap-2">
                <Label className="font-mono text-[10px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>KEY NAME</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Production KYC"
                  className="font-mono text-[12px] bg-[#090D13] border-[#182030] text-[#DCE8F5] focus-visible:ring-0 focus-visible:border-[#00C8FF]"
                />
              </div>

              {/* Tier */}
              <div className="flex flex-col gap-2">
                <Label className="font-mono text-[10px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>TIER</Label>
                <div className="flex gap-2">
                  {TIER_OPTS.map((t) => {
                    const { bg, text: col, border } = TIER_COLORS[t];
                    const active = tier === t;
                    return (
                      <button
                        key={t}
                        onClick={() => handleTierChange(t)}
                        className="flex-1 py-2 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all"
                        style={{
                          background: active ? bg : '#090D13',
                          color: active ? col : '#5A7A9A',
                          border: `1px solid ${active ? border : '#182030'}`,
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Permissions */}
              <div className="flex flex-col gap-2">
                <Label className="font-mono text-[10px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>ALLOWED MODULES</Label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(PERMISSION_LABELS) as KeyPermission[]).map((p) => (
                    <label key={p} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={perms.has(p)}
                        onCheckedChange={() => togglePerm(p)}
                        className="border-[#182030] data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                      />
                      <span className="font-mono text-[11px]" style={{ color: '#DCE8F5' }}>{PERMISSION_LABELS[p]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Rate limit */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label className="font-mono text-[10px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>RATE LIMIT / MINUTE</Label>
                  <span className="font-mono text-[11px] font-bold" style={{ color: '#00C8FF' }}>{rateLimit}</span>
                </div>
                <Slider
                  min={1} max={100} step={1}
                  value={[rateLimit]}
                  onValueChange={([v]) => setRateLimit(v)}
                  className="[&>span]:bg-[#00C8FF]"
                />
                <div className="flex justify-between">
                  <span className="text-[9px] font-mono" style={{ color: '#3A5A7A' }}>1 req/min</span>
                  <span className="text-[9px] font-mono" style={{ color: '#3A5A7A' }}>100 req/min</span>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="font-mono text-[10px] border-[#182030] text-[#5A7A9A] bg-transparent hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!name.trim()}
                className="font-mono text-[10px] bg-[#00C8FF18] text-[#00C8FF] border border-[#00C8FF30] hover:bg-[#00C8FF28] disabled:opacity-40"
              >
                <Key size={11} className="mr-1.5" /> Create Key
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-mono text-[13px] tracking-widest text-[#00C853]">Key Created</DialogTitle>
            </DialogHeader>
            <div
              className="my-2 p-4 rounded-lg"
              style={{ background: '#F0B42910', border: '1px solid #F0B42940' }}
            >
              <p className="font-mono text-[10px] font-bold mb-1" style={{ color: '#F0B429' }}>
                Save this key — it will never be shown again
              </p>
              <p className="font-mono text-[9px]" style={{ color: '#5A7A9A' }}>
                Copy and store it securely before closing this window.
              </p>
            </div>
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-lg"
              style={{ background: '#090D13', border: '1px solid #182030' }}
            >
              <code className="flex-1 font-mono text-[11px] break-all" style={{ color: '#DCE8F5' }}>
                {createdKey.key_value}
              </code>
              <button
                onClick={handleCopyNew}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-[10px] font-bold tracking-wider transition-all"
                style={{
                  background: copiedNew ? '#00C85318' : '#0F1520',
                  color: copiedNew ? '#00C853' : '#00C8FF',
                  border: `1px solid ${copiedNew ? '#00C85330' : '#00C8FF30'}`,
                }}
              >
                {copiedNew ? <Check size={11} /> : <Copy size={11} />}
                {copiedNew ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <DialogFooter>
              <Button
                onClick={handleFinish}
                className="font-mono text-[10px] bg-[#00C8FF18] text-[#00C8FF] border border-[#00C8FF30] hover:bg-[#00C8FF28]"
              >
                Done — I saved my key
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── UsageSidebar ──────────────────────────────────────────────────────────────

// TODO: wire to /api/v1/profiler/usage
const MOCK_USAGE = {
  cdd_calls: 284,
  cdd_limit: 500,
  edd_calls: 12,
  edd_limit: 50,
  reports_downloaded: 8,
  reports_limit: 25,
  credits_used: 9240,
  credits_limit: 25000,
};

function UsageSidebar() {
  const items = [
    { label: 'CDD CALLS',          value: MOCK_USAGE.cdd_calls,          limit: MOCK_USAGE.cdd_limit,          color: '#00C8FF' },
    { label: 'EDD CALLS',          value: MOCK_USAGE.edd_calls,          limit: MOCK_USAGE.edd_limit,          color: '#A855F7' },
    { label: 'REPORTS DOWNLOADED', value: MOCK_USAGE.reports_downloaded, limit: MOCK_USAGE.reports_limit,      color: '#F0B429' },
    { label: 'CREDITS USED',       value: MOCK_USAGE.credits_used,       limit: MOCK_USAGE.credits_limit,      color: '#00C853' },
  ];

  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10 }}
      className="p-5 flex flex-col gap-4"
    >
      <div className="flex items-center gap-2">
        <BarChart2 size={14} style={{ color: '#5A7A9A' }} />
        <p className="text-[9px] font-mono tracking-[0.18em]" style={{ color: '#5A7A9A' }}>USAGE THIS MONTH</p>
      </div>
      <Separator style={{ background: '#182030' }} />
      <div className="flex flex-col gap-4">
        {items.map((item) => {
          const pct = Math.min(100, Math.round((item.value / item.limit) * 100));
          return (
            <div key={item.label} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-mono tracking-[0.1em]" style={{ color: '#5A7A9A' }}>{item.label}</span>
                <span className="text-[10px] font-mono font-bold" style={{ color: item.color }}>
                  {item.value.toLocaleString()} <span style={{ color: '#3A5A7A' }}>/ {item.limit.toLocaleString()}</span>
                </span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ background: '#182030' }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: pct > 85 ? '#FF1F1F' : item.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PricingTierCard ───────────────────────────────────────────────────────────

const PRICING_ROWS = [
  { endpoint: 'CDD Check',       inr: '₹30–80',       aed: 'AED 1.5–4' },
  { endpoint: 'EDD Check',       inr: '₹300–600',     aed: 'AED 15–30' },
  { endpoint: 'Monitoring /mo',  inr: '₹150–250/mo',  aed: 'AED 7–12/mo' },
  { endpoint: 'Dossier PDF',     inr: '₹200',         aed: 'AED 10' },
];

function PricingTierCard() {
  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10 }}
      className="p-5 flex flex-col gap-4"
    >
      <p className="text-[9px] font-mono tracking-[0.18em]" style={{ color: '#5A7A9A' }}>PRICING</p>
      <Separator style={{ background: '#182030' }} />
      <table className="w-full" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['ENDPOINT', '₹ INR', 'AED'].map((h) => (
              <th key={h} className="text-left pb-2">
                <span className="text-[8px] font-mono tracking-widest" style={{ color: '#3A5A7A' }}>{h}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PRICING_ROWS.map((r) => (
            <tr key={r.endpoint} style={{ borderTop: '1px solid #0F1928' }}>
              <td className="py-2 pr-3">
                <span className="font-mono text-[10px]" style={{ color: '#DCE8F5' }}>{r.endpoint}</span>
              </td>
              <td className="py-2 pr-3">
                <span className="font-mono text-[10px]" style={{ color: '#F0B429' }}>{r.inr}</span>
              </td>
              <td className="py-2">
                <span className="font-mono text-[10px]" style={{ color: '#5A7A9A' }}>{r.aed}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── PermissionsMatrix ─────────────────────────────────────────────────────────

function MatrixCell({ val, leaOnly }: { val: boolean | 'limited'; leaOnly?: boolean }) {
  if (leaOnly) {
    return (
      <td className="py-2 px-2 text-center">
        <div className="flex items-center justify-center gap-1" title="Law Enforcement Agencies only">
          <Lock size={10} style={{ color: '#F0B429' }} />
          <span className="font-mono text-[8px]" style={{ color: '#F0B429' }}>LEA</span>
        </div>
      </td>
    );
  }
  if (val === 'limited') {
    return (
      <td className="py-2 px-2 text-center">
        <span className="font-mono text-[9px]" style={{ color: '#5A7A9A' }}>Limited</span>
      </td>
    );
  }
  if (val === true) {
    return (
      <td className="py-2 px-2 text-center">
        <CheckCircle size={13} style={{ color: '#00C853', margin: '0 auto' }} />
      </td>
    );
  }
  return (
    <td className="py-2 px-2 text-center">
      <span style={{ color: '#3A5A7A' }}>—</span>
    </td>
  );
}

function PermissionsMatrix() {
  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10 }}
      className="p-5 flex flex-col gap-4"
    >
      <div className="flex items-center gap-2">
        <Shield size={14} style={{ color: '#5A7A9A' }} />
        <p className="text-[9px] font-mono tracking-[0.18em]" style={{ color: '#5A7A9A' }}>PERMISSIONS MATRIX</p>
      </div>
      <Separator style={{ background: '#182030' }} />
      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #182030' }}>
              <th className="text-left pb-2 pr-4">
                <span className="text-[8px] font-mono tracking-widest" style={{ color: '#3A5A7A' }}>FEATURE</span>
              </th>
              {(['BASIC', 'PROFESSIONAL', 'ENTERPRISE'] as KeyTier[]).map((t) => {
                const { text: col } = TIER_COLORS[t];
                return (
                  <th key={t} className="pb-2 px-2 text-center">
                    <span className="text-[8px] font-mono tracking-widest font-bold" style={{ color: col }}>{t.slice(0, 4)}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS_MATRIX.map((row) => (
              <tr key={row.feature} style={{ borderBottom: '1px solid #0F1928' }}>
                <td className="py-2 pr-4">
                  <span className="font-mono text-[10px]" style={{ color: '#DCE8F5' }}>{row.feature}</span>
                </td>
                <MatrixCell val={row.basic} />
                <MatrixCell val={row.pro} />
                <MatrixCell val={row.ent} leaOnly={row.leaOnly} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ApiKeys() {
  const [keys, setKeys]         = useState<ApiKey[]>([EXAMPLE_KEY]);
  const [showNew, setShowNew]   = useState(false);

  function handleRevoke(id: string) {
    setKeys((prev) => prev.filter((k) => k.id !== id));
  }

  function handleCreate(key: ApiKey) {
    setKeys((prev) => [key, ...prev]);
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ background: '#050709', minHeight: '100vh' }}
      className="flex flex-col"
    >
      {/* Header */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }} className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[13px] font-mono font-bold tracking-[0.22em]" style={{ color: '#DCE8F5' }}>
              API KEYS
            </h1>
            <p className="text-[10px] font-mono mt-0.5" style={{ color: '#5A7A9A' }}>
              Manage access credentials and usage limits
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all"
            style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF30' }}
          >
            <Plus size={12} /> New Key
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 px-6 py-6">

        {/* Main column — 2/3 */}
        <div className="flex-[2] flex flex-col gap-4 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="flex flex-col gap-4"
          >
            {keys.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-16 rounded-xl"
                style={{ background: '#0C1118', border: '1px solid #182030' }}
              >
                <Key size={28} style={{ color: '#182030', marginBottom: 12 }} />
                <p className="font-mono text-[12px] font-bold" style={{ color: '#3A5A7A' }}>No API keys yet</p>
                <button
                  onClick={() => setShowNew(true)}
                  className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-[10px] font-bold tracking-widest"
                  style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF30' }}
                >
                  <Plus size={11} /> Create your first key
                </button>
              </div>
            ) : (
              keys.map((k) => (
                <ApiKeyCard key={k.id} apiKey={k} onRevoke={handleRevoke} />
              ))
            )}
          </motion.div>
        </div>

        {/* Right sidebar — 1/3 */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="lg:w-80 flex flex-col gap-4 flex-shrink-0"
        >
          <UsageSidebar />
          <PricingTierCard />
          <PermissionsMatrix />
        </motion.div>
      </div>

      <NewKeyModal open={showNew} onClose={() => setShowNew(false)} onCreate={handleCreate} />
    </motion.div>
  );
}
